<template>
  <div class="ml-2">
    <div class="row">
      <div class="col-md-4 border-right">
        <div class="mt-3">
          <request-filter
            ref="requestFilterRef"
            class="request-filter"
            @on-advance-search="onAdvanceSearchHandler"
            @on-dashboard-update="getDashboardInfo"
          />

          <request-list :items="items" />
          <div v-if="totalItemCount > pageSize" class="text-center mt-5">
            <v-pagination
              v-model="currentPage"
              :length="pageCount"
              circle
            ></v-pagination>
          </div>
        </div>
        <request-filter-toggle
          class="filter-toggle"
          :text="$t('RequestPanel')"
        />
      </div>
      <div class="col-md-8 mt-3 pt-2">
        <div class="dashboard-right-side px-0 px-md-4">
          <div class="mb-4">
            <div class="row">
              <div class="col-md-6 d-flex align-items-center">
                <img
                  class="circle-frame"
                  src="@/assets/icons/circle-user.svg"
                  alt="circle-user"
                />
                <span class="color-primary-4 font-20 font-weight-bold ml-2">
                  {{ welcomeUserName }}
                </span>
              </div>
              <div class="col-md-6 text-right pt-4 pt-md-0">
                <action-button
                  class="btn-success px-4 py-2"
                  :text="$t('CreateRequest')"
                  iright="fa fa-arrow-circle-right"
                  @click="onCreateRequestHandler"
                />
              </div>
            </div>
            <hr />
          </div>
          <div class="row">
            <div class="col">
              <card
                item-class="mb-4"
                v-for="item in requestStatusItems"
                :key="`card-status-${item.statusId}`"
                :item="item"
                @selected="onSelectedStatusHandler(item)"
              />
            </div>
            <div class="col">
              <div class="mr-2">
                <div>
                  <card
                    item-class="mb-4"
                    v-for="item in requestStatusItemsGroup"
                    :key="`card-status-group-${item.statusId}`"
                    :item="item"
                    @selected="onSelectedScopeHandler(item)"
                  />
                </div>
                <important-information v-if="isUserRoleApplicant" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create request modal confirm -->
    <create-request-modal-confirm
      ref="createRequestModalConfirmRef"
      @accept="onAcceptRequestConfirmHandler"
      @cancel="() => {}"
    />
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import { name as appModuleName } from '@/store/modules/app';
import { vuetify } from 'vuetify';
//libs
import ActionButton from 'ActionButton';

//components
import {
  Card,
  RequestFilter,
  RequestList,
  RequestFilterToggle,
  ImportantInformation,
  CreateRequestModalConfirm,
} from './components';

//services
import {
  getDashboardInfo as _getDashboardInfo,
  searchItems as _searchItems,
} from '@/services/app.api.js';

//constants
import requestStatus from '@/utils/constants/requestStatus.js';
import searchOptions from '@/utils/constants/searchOptions.js';
const CREATE_REQUEST_ROUTE_NAME = 'createRequest';

//utils
import { i18n } from '@/i18n.js';

//icons
import recentChangesIcon from '@/assets/icons/recent-changes.svg';
import recentCommentsIcon from '@/assets/icons/recent-comments.svg';
import assignedIcon from '@/assets/icons/assigned.svg';
import initialStatusIcon from '@/assets/icons/initial-status.svg';
import noFinalizeIcon from '@/assets/icons/no-finalize.svg';

//mixins
import loadingMixin from '@quisit/mipe-utils/mixins/loading';

//vuex types
import types from '@/store/modules/app/types.js';

export default {
  name: 'HomeView',
  mixins: [loadingMixin],
  components: {
    ActionButton,
    Card,
    RequestFilter,
    RequestList,
    RequestFilterToggle,
    ImportantInformation,
    CreateRequestModalConfirm,
  },

  data: () => ({
    dashboardInfo: null,
    items: [],
    currentPage: 1,
    pageCount: 1,
    totalItemCount: 0,
    pageSize: 10,
    currentStatusId: 0,
    currentStatusName: '',
    payload: {},
  }),

  computed: {
    requestStatusItems() {
      return [
        {
          statusId: requestStatus.Nuevo,
          title: 'Nueva',
          titleEn: 'New',
          count: this.evalDashValue(this.dashboardInfo?.newRequestCount),
          visible: this.isUserRoleApplicant,
          active: this.currentStatusId === requestStatus.Nuevo,
        },
        {
          statusId: requestStatus.Sometida,
          title: 'Sometida',
          titleEn: 'Submit',
          count: this.evalDashValue(this.dashboardInfo?.submitRequestCount),
          visible: true,
          active: this.currentStatusId === requestStatus.Sometida,
        },
        {
          statusId: requestStatus.EnProceso,
          title: 'En Proceso',
          titleEn: 'In Process',
          count: this.evalDashValue(this.dashboardInfo?.inProgressRequestCount),
          visible: true,
          active: this.currentStatusId === requestStatus.EnProceso,
        },
        {
          statusId: requestStatus.DevueltoPorFaltaInformacion,
          title: 'Devuelto por falta de información',
          titleEn: 'Returned for missing information',
          count: this.evalDashValue(this.dashboardInfo?.returnedRequestCount),
          visible: true,
          active:
            this.currentStatusId === requestStatus.DevueltoPorFaltaInformacion,
        },
        {
          statusId: requestStatus.Autorizado,
          title: 'Autorizada',
          titleEn: 'Authorized',
          count: this.evalDashValue(this.dashboardInfo?.authorizeRequestCount),
          visible: true,
          active: this.currentStatusId === requestStatus.Autorizado,
        },
        {
          statusId: requestStatus.RedaccionCarta,
          title: 'Redacción de Carta',
          titleEn: 'Letter Writing',
          count: this.evalDashValue(this.dashboardInfo?.mailRequestCount),
          visible: true,
          active: this.currentStatusId === requestStatus.RedaccionCarta,
        },
        {
          statusId: requestStatus.DocumentoTesis,
          title: 'Documento de Tesis',
          titleEn: 'Thesis Documentation',
          count: this.evalDashValue(this.dashboardInfo?.thesisDocument),
          visible: true,
          active: this.currentStatusId === requestStatus.DocumentoTesis,
        },
      ];
    },
    requestStatusItemsGroup() {
      return [
        {
          statusId: requestStatus.Asignadas,
          title: 'Asignadas',
          titleEn: 'Assigned',
          count: this.assignedCount,
          icon: assignedIcon,
          visible: this.isUserRoleInvestigator,
          forScope: true,
        },
        {
          statusId: requestStatus.EstadoInicial,
          title: 'En Estado Inicial',
          titleEn: 'In Initial State',
          count: this.evalDashValue(this.dashboardInfo?.alert?.newItemCount),
          icon: initialStatusIcon,
          visible: false,
          forScope: true,
        },
        {
          statusId: requestStatus.SinCerrar,
          title: 'Sin Cerrar',
          titleEn: 'Unclosed',
          count: this.evalDashValue(this.dashboardInfo?.notFinalized),
          icon: noFinalizeIcon,
          visible: false,
          forScope: true,
        },
        {
          statusId: requestStatus.DevueltoPorFaltaInformacion,
          title: 'Con Cambios Recientes',
          titleEn: 'With recent changes',
          count: this.evalDashValue(this.dashboardInfo?.statusChange),
          icon: recentChangesIcon,
          visible: true,
          forScope: true,
        },
        {
          statusId: requestStatus.EnProceso,
          title: 'Con Comentarios Recientes',
          titleEn: 'With Recent Comments',
          count: this.evalDashValue(this.dashboardInfo?.commentEvents),
          icon: recentCommentsIcon,
          visible: true,
          forScope: true,
        },
      ];
    },
    assignedCount() {
      const alert = this.dashboardInfo?.alert || {};
      return Object.keys(alert).reduce(
        (prev, current) => prev + alert[current],
        0
      );
    },
    welcomeUserName() {
      return `¡${i18n.t('Welcome')} ${this.userFullName}!`;
    },
  },
  async mounted() {
    this.restartCaseInfo();
    await this.getDashboardInfo();
  },
  watch: {
    currentPage: function (currentPage) {
      this.currentPage = currentPage;

      this.searchItems(currentPage);
    },
  },
  methods: {
    ...mapMutations(appModuleName, [
      'setAssignedRequests',
      types.SET_SITEMAP_INFO,
    ]),
    openSidebar() {
      this.$root.$emit('bv::toggle::collapse', 'my-sidebar');
    },
    onCreateRequestHandler() {
      this.$refs.createRequestModalConfirmRef?.showModal();
    },
    onAcceptRequestConfirmHandler() {
      this.$refs.createRequestModalConfirmRef?.hideModal();
      this.$router.push({
        name: CREATE_REQUEST_ROUTE_NAME,
      });
    },
    async getDashboardInfo() {
      this.ShowWait();
      const filters = this.$refs.requestFilterRef?.filter || {};
      this.dashboardInfo = (await _getDashboardInfo(filters)).data;
      //Validate the dashboard info is not null and is not undefined the assignedRequests property
      this.dashboardInfo.assignedRequests =
        this.dashboardInfo?.assignedRequests || 0;

      this[types.SET_SITEMAP_INFO]({
        assignedRequestCount: this.dashboardInfo.assignedRequests,
        routes: [i18n.t('Home'), i18n.t('Dashboard')],
      });
      this.HideWait();
    },
    evalDashValue(value) {
      return value ? value : 0;
    },
    async onAdvanceSearchHandler(filter) {
      const payload = {
        searchText: filter.searchText,
        requesterName: filter.requesterName,
        dateFrom: filter.dateFrom,
        dateTo: filter.dateTo,
        statusId: filter.status?.id,
        email: filter.email,
        phone: filter.phone,
        searchScope: filter.searchScope?.id || searchOptions.Todos,
        subType: filter.subType?.id,
        regionId: filter.region?.id,
      };
      this.payload = payload;
      try {
        console.log(this.currentPage, payload);
        if (this.currentPage == undefined) {
          this.currentPage = 1;
        }

        const { data: response } = await _searchItems(
          payload,
          this.currentpage
        );
        console.log(response);
        this.items = response?.data || [];
        this.currentPage = response?.currentPage;
        this.pageCount = response?.pageCount;
        this.totalItemCount = response?.totalItemCount;
      } catch (error) {
        console.log(error);
      }
    },
    async searchItems(currentPage) {
      console.log(currentPage);
      if (currentPage == undefined) {
        currentPage = 1;
      }
      const { data: response } = await _searchItems(this.payload, currentPage);
      this.items = response?.data || [];
      this.currentPage = response?.currentPage;
      this.pageCount = response?.pageCount;
      this.totalItemCount = response?.totalItemCount;
    },
    applyDynamicRequestFilter(prop) {
      if (Object.keys(prop).length) {
        this.$refs.requestFilterRef.filter = {
          ...prop,
        };
        // card click: filter list + highlight, do NOT refetch dashboard counts
        this.$refs.requestFilterRef.onAdvanceSearchHandler(false, false);
      }
    },
    onSelectedStatusHandler(value) {
      if (value?.statusId) {
        this.applyDynamicRequestFilter({
          status: {
            id: value.statusId,
            name: value.title,
          },
          searchScope: this.isUserRoleApplicant
            ? {
                id: searchOptions.CreadosPorMi,
                name: 'Creados por Mi',
              }
            : this.isUserRoleInvestigator
            ? {
                id: searchOptions.SolicitudesAsignadas,
                name: 'Asignadas Ami',
              }
            : null,
        });
        this.currentStatusId = value.statusId;
        // this.currentPage = value.title;
      }
    },

    onSelectedScopeHandler(value) {
      if (value?.statusId) {
        //Asignadas
        if (value.statusId === requestStatus.Asignadas) {
          this.applyDynamicRequestFilter({
            searchScope: {
              id: searchOptions.SolicitudesAsignadas,
              name: 'Solicitudes Asignadas',
            },
          });
        }
        //En Estado Inicial
        if (value.statusId === requestStatus.EstadoInicial) {
          this.applyDynamicRequestFilter({
            status: {
              id: requestStatus.Nuevo,
              name: 'Nuevo',
            },
          });
        }
      }
    },
    restartCaseInfo() {
      this[types.SET_ITEM_MODEL]({});
      this.setIsNavOpen({
        isNavOpen: false,
        navOption: {},
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/scss/components/action-button/index.scss';

.circle-frame {
  background: #ffffff;
  box-shadow: 0px 3px 6px #00000029;
  opacity: 1;
  border: 1px solid #fff;
  border-radius: 100px;
  padding: 3px;
}

.dashboard-right-side {
  margin-left: 20px;
}

@media only screen and (max-width: 768px) {
  .dashboard-right-side {
    margin-left: 0px;
  }

  .request-filter {
    margin-top: 50px;
  }

  .filter-toggle {
    display: none;
  }
}
</style>
